import { Link } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';

export default function PuttingGreenTurfPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block bg-green-700 text-white text-xs font-bold px-4 py-2 rounded-md mb-6 uppercase tracking-wide">Putting Green Turf</span>
              <h1 className="text-6xl font-bold text-gray-900 mb-6 leading-tight">Practice Like The Pros At Home</h1>
              <p className="text-xl text-gray-700 mb-8 leading-relaxed">
                Professional-grade putting green turf for your Tulsa backyard. Perfect your short game with realistic ball roll and consistent speed—just like championship golf courses.
              </p>
              <div className="flex flex-wrap gap-5">
                <a href="tel:918-806-8873" className="bg-green-700 text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-green-800 transition-all hover:scale-105 whitespace-nowrap">
                  Get A Quote
                </a>
                <Link to="/about" className="bg-transparent border-2 border-green-700 text-green-700 px-8 py-4 rounded-full text-base font-semibold hover:bg-green-700 hover:text-white transition-all whitespace-nowrap">
                  Learn More
                </Link>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://public.readdy.ai/ai/img_res/181bf928a8cb4925fa5fc0c7e2f4d265.jpg" 
                alt="Putting Green Turf Tulsa Oklahoma"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Why Install A Backyard Putting Green</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Transform your Tulsa property into a golfer's paradise</p>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-7">
                <i className="ri-home-heart-line text-6xl text-green-700"></i>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Practice Anytime</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Perfect your putting stroke whenever you want. No driving to the course, no waiting for tee times. Step into your backyard and practice day or night.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-7">
                <i className="ri-trophy-line text-6xl text-green-700"></i>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Lower Your Scores</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Consistent practice on a quality putting surface improves your game. Most golfers can drop 3-5 strokes by improving their short game through regular practice.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-7">
                <i className="ri-home-smile-line text-6xl text-green-700"></i>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Increase Property Value</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                A custom putting green is a premium amenity that enhances curb appeal and property value. It's a unique feature that sets your Tulsa home apart.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-7">
                <i className="ri-group-line text-6xl text-green-700"></i>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Entertainment Value</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Host putting competitions, entertain guests, and create memorable experiences. A putting green becomes the centerpiece of outdoor gatherings.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-7">
                <i className="ri-time-line text-6xl text-green-700"></i>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Zero Maintenance</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Unlike natural grass greens, synthetic putting surfaces require no mowing, watering, or fertilizing. Just brush occasionally and enjoy perfect conditions.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-7">
                <i className="ri-sun-line text-6xl text-green-700"></i>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Year-Round Perfection</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Your putting green stays perfect through Oklahoma's hot summers, cold winters, and everything in between. Consistent speed and roll every day of the year.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-5xl font-bold text-gray-900 mb-6">Professional-Grade Performance</h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <i className="ri-checkbox-circle-fill text-3xl text-green-700"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">True Ball Roll</h3>
                    <p className="text-base text-gray-600 leading-relaxed">
                      Engineered for realistic ball roll that mimics championship golf courses. Consistent speed and direction help you develop accurate distance control and reading skills.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <i className="ri-checkbox-circle-fill text-3xl text-green-700"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Customizable Speed</h3>
                    <p className="text-base text-gray-600 leading-relaxed">
                      Choose your preferred stimp speed from 8 to 13+ feet. Match your home course conditions or practice on faster tournament-speed greens.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <i className="ri-checkbox-circle-fill text-3xl text-green-700"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Contour &amp; Break Options</h3>
                    <p className="text-base text-gray-600 leading-relaxed">
                      Add undulations, slopes, and breaks to create challenging practice scenarios. We can design greens with multiple break directions and elevation changes.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <i className="ri-checkbox-circle-fill text-3xl text-green-700"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Multiple Cup Locations</h3>
                    <p className="text-base text-gray-600 leading-relaxed">
                      Install multiple cups to vary your practice routine. Change hole positions to practice different putts and keep your sessions interesting.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <i className="ri-checkbox-circle-fill text-3xl text-green-700"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Fringe &amp; Chipping Areas</h3>
                    <p className="text-base text-gray-600 leading-relaxed">
                      Add fringe areas with different turf heights for chipping practice. Create a complete short game practice facility in your Tulsa backyard.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=close-up%20of%20professional%20putting%20green%20artificial%20synthetic%20golf%20turf%2C%20realistic%20golf%20course%20grass%20texture%2C%20smooth%20putting%20surface%20with%20golf%20ball%2C%20detailed%20view%20of%20quality%20golf%20turf%2C%20clean%20simple%20background&width=800&height=900&seq=puttingdetail918&orientation=portrait" 
                alt="Putting Green Turf Detail"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Design Options */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Custom Design Options</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Every putting green is unique—designed to fit your space and skill development goals</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8" data-product-shop>
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow">
              <div className="relative h-64 w-full">
                <img 
                  src="https://readdy.ai/api/search-image?query=small%20compact%20backyard%20putting%20green%20with%20artificial%20turf%20in%20Tulsa%20Oklahoma%2C%20simple%20putting%20green%20design%2C%20residential%20yard%20with%20golf%20practice%20area%2C%20clean%20simple%20background&width=600&height=600&seq=compact918&orientation=squarish" 
                  alt="Compact Putting Green"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Compact Greens</h3>
                <p className="text-sm text-gray-600">Perfect for smaller yards—300-500 sq ft</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow">
              <div className="relative h-64 w-full">
                <img 
                  src="https://readdy.ai/api/search-image?query=large%20custom%20putting%20green%20with%20artificial%20turf%20in%20Tulsa%20Oklahoma%20backyard%2C%20expansive%20golf%20practice%20area%20with%20multiple%20holes%2C%20professional%20home%20putting%20green%2C%20clean%20simple%20background&width=600&height=600&seq=large918&orientation=squarish" 
                  alt="Large Putting Green"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Large Greens</h3>
                <p className="text-sm text-gray-600">Expansive practice areas—1000+ sq ft</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow">
              <div className="relative h-64 w-full">
                <img 
                  src="https://readdy.ai/api/search-image?query=contoured%20putting%20green%20with%20slopes%20and%20breaks%20artificial%20turf%20in%20Tulsa%20Oklahoma%2C%20challenging%20golf%20practice%20green%20with%20undulations%2C%20professional%20design%2C%20clean%20simple%20background&width=600&height=600&seq=contour918&orientation=squarish" 
                  alt="Contoured Putting Green"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Contoured Greens</h3>
                <p className="text-sm text-gray-600">Challenging slopes and undulations</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow">
              <div className="relative h-64 w-full">
                <img 
                  src="https://readdy.ai/api/search-image?query=putting%20green%20with%20chipping%20area%20artificial%20turf%20in%20Tulsa%20Oklahoma%2C%20complete%20short%20game%20practice%20facility%2C%20fringe%20and%20putting%20surface%2C%20clean%20simple%20background&width=600&height=600&seq=chipping918&orientation=squarish" 
                  alt="Chipping Green"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Chipping Greens</h3>
                <p className="text-sm text-gray-600">Complete short game practice areas</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Installation Process */}
      <section className="py-24 bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Professional Installation Process</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">From design to completion, we handle every detail</p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <div className="w-16 h-16 flex items-center justify-center bg-green-700 rounded-full mb-6">
                <span className="text-3xl font-bold text-white">1</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Design Consultation</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                We visit your Tulsa property to assess space, discuss your goals, and design a custom putting green that fits your yard and budget.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <div className="w-16 h-16 flex items-center justify-center bg-green-700 rounded-full mb-6">
                <span className="text-3xl font-bold text-white">2</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Site Preparation</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Excavation, grading, and base installation create the perfect foundation. We build in drainage and contours according to your design.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <div className="w-16 h-16 flex items-center justify-center bg-green-700 rounded-full mb-6">
                <span className="text-3xl font-bold text-white">3</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Turf Installation</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Professional installation of putting green turf with precise seaming, cup placement, and edge finishing for a flawless appearance.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <div className="w-16 h-16 flex items-center justify-center bg-green-700 rounded-full mb-6">
                <span className="text-3xl font-bold text-white">4</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Final Details</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Infill application, speed adjustment, and final grooming ensure optimal performance. We teach you maintenance and care.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-green-700 to-green-900">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">Ready To Build Your Dream Putting Green?</h2>
          <p className="text-xl text-white/90 mb-10 max-w-3xl mx-auto leading-relaxed">
            Get a free consultation and quote for a custom putting green. We'll design the perfect practice facility for your Tulsa property.
          </p>
          <div className="flex flex-wrap justify-center gap-5">
            <a href="tel:918-806-8873" className="bg-white text-green-700 px-10 py-5 rounded-full text-lg font-bold hover:bg-gray-100 transition-all hover:scale-105 whitespace-nowrap">
              Call 918-806-8873
            </a>
            <Link to="/about" className="bg-transparent border-2 border-white text-white px-10 py-5 rounded-full text-lg font-bold hover:bg-white hover:text-green-700 transition-all whitespace-nowrap">
              Learn More
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}